
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import { useState, useEffect } from "react";
import { toast } from "sonner";
import { Booking, BookingStatus } from "./types";

export const useBookings = () => {
  const queryClient = useQueryClient();
  const [isConfirmingPayment, setIsConfirmingPayment] = useState(false);
  const [selectedBookingId, setSelectedBookingId] = useState<string | null>(null);

  const { data: userBookings = [], refetch } = useQuery({
    queryKey: ['userBookings'],
    queryFn: async () => {
      const { data: userSession } = await supabase.auth.getSession();
      if (!userSession.session) return [];

      // First get the booking statuses from the view
      const { data: bookingStatuses, error: statusError } = await supabase
        .from('booking_status_view')
        .select('*')
        .eq('user_id', userSession.session.user.id);

      if (statusError) {
        console.error('Error fetching booking statuses:', statusError);
        return [];
      }

      // Then get the full booking details including time slots and guests
      const { data: bookings, error: bookingsError } = await supabase
        .from('bookings')
        .select(`
          *,
          time_slot:time_slots_created(
            date,
            start_time,
            fee
          ),
          guests(
            id,
            name,
            skill_level,
            gender
          )
        `)
        .eq('user_id', userSession.session.user.id)
        .order('date', { foreignTable: 'time_slots_created', ascending: true })
        .order('start_time', { foreignTable: 'time_slots_created', ascending: true });

      if (bookingsError) {
        console.error('Error fetching bookings:', bookingsError);
        return [];
      }

      // Transform the data to match the expected types
      return bookings.map(booking => {
        const status = bookingStatuses?.find(
          (status: BookingStatus) => status.booking_id === booking.id
        );
        return {
          ...booking,
          status: status?.payment_status || 'pending payment',
          guests: (booking.guests || []).map(guest => ({
            id: guest.id,
            name: guest.name,
            skillLevel: guest.skill_level, // Map skill_level to skillLevel
            gender: guest.gender
          }))
        } as unknown as Booking; // Type assertion after transformation
      });
    },
  });

  useEffect(() => {
    const setupSubscription = async () => {
      const { data: userSession } = await supabase.auth.getSession();
      if (!userSession.session) return;

      const channel = supabase
        .channel('bookings_changes')
        .on(
          'postgres_changes',
          {
            event: '*',
            schema: 'public',
            table: 'booking_status_view',
            filter: `user_id=eq.${userSession.session.user.id}`
          },
          (payload) => {
            console.log('Booking status changed, refreshing...', payload);
            refetch();
          }
        )
        .subscribe();

      return () => {
        channel.unsubscribe();
      };
    };

    setupSubscription();
  }, [refetch]);

  const handleCancelBooking = async (bookingId: string) => {
    const { error } = await supabase
      .from('bookings')
      .delete()
      .eq('id', bookingId);

    if (error) {
      toast.error("Failed to cancel booking. Please try again.");
      return;
    }

    queryClient.invalidateQueries({ queryKey: ['userBookings'] });
    queryClient.invalidateQueries({ queryKey: ['timeSlotsToSelect'] });
    toast.success("Booking cancelled successfully");
  };

  const handleConfirmPayment = async (bookingId: string) => {
    setIsConfirmingPayment(true);
    try {
      const { error: paymentError } = await supabase
        .from('payment_status')
        .update({ 
          status: 'paid', 
          payment_date: new Date().toISOString() 
        })
        .eq('booking_id', bookingId);

      if (paymentError) {
        console.error('Payment status update error:', paymentError);
        toast.error("Error updating payment and booking. Please try again.");
        return;
      }

      toast.success("Payment confirmed!");
      setSelectedBookingId(bookingId);
      
      // Invalidate and immediately refetch to ensure UI updates
      await Promise.all([
        queryClient.invalidateQueries({ queryKey: ['userBookings'] }),
        queryClient.invalidateQueries({ queryKey: ['paymentStatuses'] })
      ]);
      await refetch();
      
    } catch (error) {
      console.error('Payment confirmation error:', error);
      toast.error("Error updating payment and booking. Please try again.");
    } finally {
      setIsConfirmingPayment(false);
    }
  };

  return {
    userBookings,
    isConfirmingPayment,
    selectedBookingId,
    handleCancelBooking,
    handleConfirmPayment,
  };
};
